import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MdOutlinePhone, MdOutlinePersonOutline } from "react-icons/md";
import { authAPI } from "services/api";
import { useAuth } from "context/AuthContext";

const countries = [
    { name: "Türkiye", code: "+90", flag: "🇹🇷", sample: "5xx xxx xxxx" },
    { name: "Almanya", code: "+49", flag: "🇩🇪", sample: "15x xxxx xxxx" },
    { name: "ABD", code: "+1", flag: "🇺🇸", sample: "201 xxx xxxx" },
    { name: "İngiltere", code: "+44", flag: "🇬🇧", sample: "7400 xxxxxx" },
    { name: "Rusya", code: "+7", flag: "🇷🇺", sample: "9xx xxx xxxx" },
    { name: "Suudi Arabistan", code: "+966", flag: "🇸🇦", sample: "5x xxx xxxx" },
];

const sanitizePhone = (v) => v.replace(/[^\d]/g, "").slice(0, 10);

export default function FastLogin() {
    const navigate = useNavigate();
    const { login } = useAuth();

    const [formData, setFormData] = useState({ name: "", surname: "", phone: "" });
    const [countryCode, setCountryCode] = useState("+90");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
    const handlePhoneChange = (e) => setFormData({ ...formData, phone: sanitizePhone(e.target.value) });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        if (formData.phone.length !== 10 || (countryCode === "+90" && !formData.phone.startsWith("5"))) {
            setError("Lütfen geçerli bir telefon numarası giriniz.");
            setLoading(false);
            return;
        }
        if (formData.name.trim().length < 2 || formData.surname.trim().length < 2) {
            setError("Lütfen ad ve soyadınızı eksiksiz giriniz.");
            setLoading(false);
            return;
        }

        try {
            const fullPhone = countryCode + formData.phone;
            const dataToSend = { name: formData.name, surname: formData.surname, phone: fullPhone };
            const response = await authAPI.fastLogin(dataToSend);
            login(response.user, response.token);
            navigate(`/${response.user.role}/dashboard`);
        } catch (err) {
            setError(err.response?.data?.message || "İşlem başarısız. Lütfen bilgilerinizi kontrol edin.");
        } finally {
            setLoading(false);
        }
    };

    const placeholder = countries.find((c) => c.code === countryCode)?.sample || "5xx xxx xxxx";

    return (
        <div className="mt-16 mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
            <div className="mt-[5vh] w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
                {/* Başlık Alanı */}
                <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">Hızlı Giriş</h4>
                <p className="mb-9 ml-1 text-base text-gray-600 dark:text-gray-400">
                    Şifresiz, sadece adınız ve telefonunuzla hemen başlayın.
                </p>

                {error && (
                    <div className="mb-4 flex items-center rounded-xl bg-red-100 p-3 text-sm font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                        <span className="mr-2 text-lg">⚠️</span> {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="w-full">
                    {/* Ad & Soyad Grid Yapısı */}
                    <div className="mb-4 grid grid-cols-2 gap-4">
                        <div>
                            <label className="mb-2 ml-1.5 block text-sm font-medium text-navy-700 dark:text-white">Ad</label>
                            <div className="relative">
                                <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400">
                                    <MdOutlinePersonOutline className="h-5 w-5" />
                                </div>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Adınız"
                                    value={formData.name}
                                    onChange={handleChange}
                                    disabled={loading}
                                    required
                                    className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 py-3 pl-10 pr-3 text-sm outline-none transition-all hover:border-brand-500 focus:border-brand-500 dark:border-white/10 dark:text-white dark:hover:border-white/20"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="mb-2 ml-1.5 block text-sm font-medium text-navy-700 dark:text-white">Soyad</label>
                            <div className="relative">
                                <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400">
                                    <MdOutlinePersonOutline className="h-5 w-5" />
                                </div>
                                <input
                                    type="text"
                                    name="surname"
                                    placeholder="Soyadınız"
                                    value={formData.surname}
                                    onChange={handleChange}
                                    disabled={loading}
                                    required
                                    className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 py-3 pl-10 pr-3 text-sm outline-none transition-all hover:border-brand-500 focus:border-brand-500 dark:border-white/10 dark:text-white dark:hover:border-white/20"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Telefon Numarası Alanı */}
                    <label className="mb-2 ml-1.5 block text-sm font-medium text-navy-700 dark:text-white">
                        Telefon Numarası
                    </label>
                    <div className="mb-6 flex gap-3">
                        <div className="relative w-[130px]">
                            <select
                                value={countryCode}
                                onChange={(e) => setCountryCode(e.target.value)}
                                disabled={loading}
                                className="flex h-12 w-full appearance-none items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none transition-all hover:border-green-500 focus:border-green-500 dark:border-white/10 dark:text-white dark:bg-navy-900 dark:hover:border-white/20"
                            >
                                {countries.map((c) => (
                                    <option key={c.code} value={c.code} className="dark:bg-navy-900">
                                        {c.flag} {c.code}
                                    </option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                </svg>
                            </div>
                        </div>
                        <div className="relative flex-1">
                            <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400">
                                <MdOutlinePhone className="h-5 w-5" />
                            </div>
                            <input
                                type="tel"
                                name="phone"
                                placeholder={placeholder}
                                value={formData.phone}
                                onChange={handlePhoneChange}
                                disabled={loading}
                                required
                                className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 py-3 pl-10 pr-3 text-sm outline-none transition-all hover:border-green-500 focus:border-green-500 dark:border-white/10 dark:text-white dark:hover:border-white/20"
                            />
                        </div>
                    </div>

                    {/* Aksiyon Butonu - Yeşil Tonlu Hızlı Giriş Konsepti */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="linear mt-2 flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-green-500 to-green-600 py-[12px] text-base font-medium text-white transition duration-200 hover:from-green-600 hover:to-green-700 active:from-green-700 active:to-green-800 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-500"
                    >
                        {loading ? (
                            <svg className="mr-2 h-5 w-5 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            "Hızlı Giriş Yap"
                        )}
                    </button>

                    <div className="mt-6 text-center text-sm">
                        <p className="text-navy-700 dark:text-gray-400">
                            Normal giriş yapmak için{" "}
                            <Link to="/auth/sign-in" className="font-bold text-brand-500 hover:text-brand-600 dark:text-brand-400">
                                tıklayın
                            </Link>
                        </p>
                        <p className="mt-3 text-xs text-gray-500 dark:text-gray-500">
                            Hızlı giriş yaparak kullanım koşullarını kabul etmiş olursunuz.
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
}